import { useCallback, useReducer, DependencyList } from 'react'
import locales from '../../locales.en.json'
import { TransactionAction, TransactionState, TransactionStatus } from './types'
import res from './res'
import type { ContractTransaction } from '@ethersproject/contracts'
import { useWeb3Provider } from '../../components/Web3ContextProvider'
import type { PrpsDubiWallet } from '../../wallet/PrpsDubiWallet'
import { useMountedState } from 'react-use'
import { WalletModalErrorCodes } from '../../types'

const initialState: TransactionState = {
  loading: false,
  txStatus: TransactionStatus.NOT_STARTED,
  data: {
    submittedTx: undefined,
    chainId: undefined,
    receipt: undefined,
    message: '',
  },
  error: undefined,
  // Internal to the hook only, if submitting
  // multiple times, this check will ensure
  // state reset on each submit.
  submitCount: 0,
}

function reducer(state: TransactionState, action: TransactionAction): TransactionState {
  switch (action.type) {
    case 'loading':
      return { ...state, loading: true }
    case 'submit':
      return {
        ...state,
        txStatus: TransactionStatus.SUBMITTED,
        data: {
          ...state.data,
          submittedTx: action.transaction,
          chainId: action.chainId,
          message: action.message,
        },
      }
    case 'complete':
      return {
        ...state,
        loading: false,
        txStatus: TransactionStatus.COMPLETE,
        data: {
          ...state.data,
          receipt: action.receipt,
        },
        submitCount: state.submitCount + 1,
      }
    case 'failure':
      return {
        ...state,
        loading: false,
        txStatus: action.transactionStatus,
        data: {
          ...state.data,
          message: typeof action.error === 'string' ? action.error : '',
        },
        error: {
          detail: typeof action.error === 'string' ? new Error(action.error) : action.error,
          code: action.code,
        },
        submitCount: state.submitCount + 1,
      }
    case 'reset':
      return initialState
    default:
      return {
        ...state,
        txStatus: TransactionStatus.FAILED,
        loading: false,
        data: {
          ...state.data,
          message: locales.WalletTxSomethingWentWrong,
        },
        submitCount: state.submitCount + 1,
        error: {
          detail: new Error('There is no way this happened, Reducer panic'),
          code: WalletModalErrorCodes.General,
        },
      }
  }
}

export default function useTransaction(
  contractCall: (provider: PrpsDubiWallet) => Promise<ContractTransaction | undefined>,
  deps: DependencyList,
  awaitReceipt?: boolean
) {
  const isMounted = useMountedState()
  const { provider, chainId } = useWeb3Provider()
  const [{ loading, data, error, txStatus, submitCount }, dispatch] = useReducer(
    reducer,
    initialState
  )
  const safeDispatch = (action: TransactionAction) => {
    isMounted() && dispatch(action)
  }
  const submit = useCallback(async () => {
    // Check Wallet modal playground
    // to see the hook behavior.
    if (submitCount > 0) {
      safeDispatch({ type: 'reset' })
    }

    if (!chainId) {
      safeDispatch(res({ code: WalletModalErrorCodes.ChainIdNotFound }).txNoChainId)
      return
    }

    if (!provider) {
      safeDispatch(res({ code: WalletModalErrorCodes.ProviderNotFound }).txNoWallet)
      return
    }

    safeDispatch(res().txLoading)

    try {
      const tx = await contractCall(provider)

      if (!tx) {
        safeDispatch(
          res({
            code: WalletModalErrorCodes.General,
            error: new Error('Something went wrong when trying to submit the transaction'),
          }).txSubmitFail
        )
        return
      }

      safeDispatch(res({ transaction: tx, chainId }).txSubmitSuccess)

      /**
       * We can also await the receipt from within the hook
       * if we do not plan to unmount the component which
       * we are submitting the transaction from
       *
       * otherwise needs to be awaited from the
       * `data.transaction` state
       *
       */
      if (awaitReceipt) {
        const receipt = await tx.wait(1)

        safeDispatch(res({ receipt }).txComplete)
      }
    } catch (error: any) {
      switch (error.code) {
        case WalletModalErrorCodes.UserRejectedRequest:
          safeDispatch(
            res({
              code: WalletModalErrorCodes.UserRejectedRequest,
              error: new Error('Transaction signature rejected'),
            }).txError
          )
          break
        case WalletModalErrorCodes.ResourceUnavailable:
          safeDispatch(
            res({
              code: WalletModalErrorCodes.ResourceUnavailable,
              error: new Error('Transaction signature request already pending'),
            }).txError
          )
          break
        default:
          safeDispatch(
            res({
              code: WalletModalErrorCodes.General,
              error: new Error('Something went wrong when trying to submit the transaction'),
            }).txError
          )
          break
      }
    }
  }, [chainId, provider, ...deps])

  return { loading, data, error, txStatus, submit, reset: () => dispatch({ type: 'reset' }) }
}
